(() => {
  'use strict';
  const labels = window.fodReviewLabels;
  if (!labels) return;
  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    element.className = className;
    if (text) element.textContent = text;
    return element;
  };
  const enhance = (source) => {
    if (source.dataset.fodEnhanced) return true;
    const unique = new Map();
    source.querySelectorAll('.jdgm-carousel-item[data-review-id]').forEach(item => {
      if (!unique.has(item.dataset.reviewId)) unique.set(item.dataset.reviewId, item);
    });
    if (!unique.size) return false;
    const section = make('section', 'fod-reviews');
    const heading = make('header', 'fod-reviews__heading');
    const intro = make('div', 'fod-reviews__intro');
    intro.append(make('p', 'fod-reviews__eyebrow', labels.eyebrow));
    const title = make('h2', 'fod-reviews__title', labels.title);
    title.id = 'fod-reviews-title';
    section.setAttribute('aria-labelledby', title.id);
    intro.append(title);
    const meta = make('div', 'fod-reviews__meta');
    const score = Number(source.querySelector('[data-score]')?.dataset.score);
    const count = source.querySelector('[data-number-of-reviews]')?.dataset.numberOfReviews;
    if (score > 0) {
      meta.append(make('span', 'fod-reviews__score', score.toLocaleString(document.documentElement.lang, {maximumFractionDigits: 2}) + ' / 5'));
    }
    if (count) meta.append(make('span', 'fod-reviews__count', labels.count.replace('[count]', count)));
    heading.append(intro, meta);
    const track = make('div', 'fod-reviews__track');
    track.id = 'fod-reviews-track';
    track.tabIndex = 0;
    track.setAttribute('role', 'list');
    const entries = Array.from(unique).sort((a, b) => Number(Boolean(b[1].querySelector('.jdgm-carousel-item__product-image'))) - Number(Boolean(a[1].querySelector('.jdgm-carousel-item__product-image'))));
    for (const [id, item] of entries) {
      const card = make('article', 'fod-review');
      card.dataset.reviewId = id;
      card.setAttribute('role', 'listitem');
      const photo = item.querySelector('.jdgm-carousel-item__product-image');
      if (photo) {
        const frame = make('div', 'fod-review__photo');
        const img = make('img', 'fod-review__image');
        img.alt = photo.alt;
        const raw = photo.dataset.srcRetina || photo.dataset.src || photo.src;
        try {
          const url = new URL(raw, location.origin);
          if (url.hostname === 'cdn.shopify.com') {
            url.pathname = url.pathname.replace(/_(?:70x70|140x140)(?=\.[a-z]+$)/i, '');
            url.searchParams.set('width', '520');
          }
          img.src = url.href;
        } catch { img.src = raw; }
        img.width = 520;
        img.height = 520;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('error', () => { frame.hidden = true; }, {once: true});
        frame.append(img);
        card.append(frame);
      } else {
        const frame = make('div', 'fod-review__photo fod-review__photo--store');
        frame.append(make('span', 'fod-review__monogram', 'FD'), make('span', 'fod-review__store-label', labels.store));
        card.append(frame);
      }
      const content = make('div', 'fod-review__content');
      const ratingSource = item.querySelector('.jdgm-carousel-item__review-rating');
      if (ratingSource) {
        const rating = make('div', 'fod-review__rating');
        const stars = ratingSource.querySelectorAll('.jdgm--on').length;
        rating.textContent = '★'.repeat(stars) + '☆'.repeat(5 - stars);
        rating.setAttribute('role', 'img');
        rating.setAttribute('aria-label', ratingSource.getAttribute('aria-label') || String(stars));
        content.append(rating);
      }
      const reviewTitle = item.querySelector('.jdgm-carousel-item__review-title')?.textContent.trim();
      if (reviewTitle) content.append(make('h3', 'fod-review__title', reviewTitle));
      const body = item.querySelector('.jdgm-carousel-item__review-body')?.textContent.trim();
      if (body) {
        const quote = make('blockquote', 'fod-review__quote', body);
        content.append(quote);
        if (body.length > 180) {
          quote.classList.add('fod-review__quote--collapsed');
          const more = make('button', 'fod-review__more', labels.more);
          more.type = 'button';
          more.setAttribute('aria-expanded', 'false');
          more.addEventListener('click', () => {
            const collapsed = quote.classList.toggle('fod-review__quote--collapsed');
            more.setAttribute('aria-expanded', String(!collapsed));
            more.textContent = collapsed ? labels.more : labels.less;
          });
          content.append(more);
        }
      }
      const footer = make('footer', 'fod-review__footer');
      const name = item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent.trim();
      if (name) footer.append(make('p', 'fod-review__author', name));
      if (photo?.alt) footer.append(make('p', 'fod-review__product', photo.alt));
      content.append(footer);
      card.append(content);
      track.append(card);
    }
    const controls = make('div', 'fod-reviews__controls');
    const hint = make('span', 'fod-reviews__hint', labels.hint);
    const actions = make('div', 'fod-reviews__actions');
    const buttons = [-1, 1].map(direction => {
      const button = make('button', 'fod-reviews__arrow', direction < 0 ? '←' : '→');
      button.type = 'button';
      button.setAttribute('aria-label', direction < 0 ? labels.previous : labels.next);
      button.setAttribute('aria-controls', track.id);
      button.addEventListener('click', () => track.scrollBy({left: direction * (track.firstElementChild.getBoundingClientRect().width + 20), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'}));
      actions.append(button);
      return button;
    });
    const update = () => {
      buttons[0].disabled = track.scrollLeft <= 2;
      buttons[1].disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    track.addEventListener('scroll', update, {passive: true});
    new ResizeObserver(update).observe(track);
    controls.append(hint, actions);
    section.append(heading, track, controls);
    source.before(section);
    source.dataset.fodEnhanced = 'true';
    source.classList.add('fod-reviews-source');
    requestAnimationFrame(update);
    return true;
  };
  let observer;
  const init = () => {
    let ready = false;
    document.querySelectorAll('.jdgm-carousel-wrapper').forEach(source => { ready = enhance(source) || ready; });
    if (ready) observer?.disconnect();
    return ready;
  };
  const start = () => {
    if (init()) return;
    observer = new MutationObserver(init);
    observer.observe(document.getElementById('PageContainer') || document.body, {childList: true, subtree: true});
    setTimeout(() => observer.disconnect(), 30000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
  document.addEventListener('shopify:section:load', init);
})();
